/**
 * Leaderboard Visualization Component (DASH-7.2)
 *
 * Renders ranked user performance data with medal icons for top 3 positions.
 * Features pagination, loading states, and responsive design.
 * Wrapped in React.memo for performance optimization.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Medal, Trophy, AlertTriangle, Inbox } from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import type { DynamicWidgetConfig, QueryResult } from '@/types/dashboard';
import type { VisualizationClickEvent } from './DynamicVisualization';

interface LeaderboardVisualizationProps {
  data: QueryResult;
  config: DynamicWidgetConfig;
  /** Callback when a row is clicked (DASH-24.2) */
  onSegmentClick?: (event: VisualizationClickEvent) => void;
}

const ITEMS_PER_PAGE = 10;

/**
 * Format metric value based on aggregation type
 */
const formatValue = (value: number, aggregationType: string): string => {
  if (aggregationType === 'count') {
    return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  } else {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
};

/**
 * Get display name for data source
 */
const getDataSourceLabel = (dataSource: string): string => {
  return dataSource.charAt(0).toUpperCase() + dataSource.slice(1);
};

/**
 * Medal colors for top 3 positions
 */
const medalColors = {
  1: '#FFD700', // Gold
  2: '#C0C0C0', // Silver
  3: '#CD7F32', // Bronze
};

/**
 * Render rank indicator (medal or number)
 */
const RankIndicator = ({ rank }: { rank: number }) => {
  if (rank <= 3) {
    return <Medal className="w-6 h-6" style={{ color: medalColors[rank as 1 | 2 | 3] }} />;
  }
  return <span className="text-muted-foreground">#{rank}</span>;
};

/**
 * Loading skeleton component
 */
const LoadingSkeleton = ({ dataSourceLabel }: { dataSourceLabel: string }) => (
  <div className="flex flex-col h-full">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-20">Rank</TableHead>
          <TableHead>Name</TableHead>
          <TableHead className="text-right">{dataSourceLabel}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Array.from({ length: 10 }).map((_, idx) => (
          <TableRow key={idx}>
            <TableCell>
              <div className="h-4 w-8 bg-gray-200 rounded animate-pulse" />
            </TableCell>
            <TableCell>
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
            </TableCell>
            <TableCell>
              <div className="h-4 w-16 bg-gray-200 rounded animate-pulse ml-auto" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

/**
 * Empty state component
 */
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-64 text-center">
    <Inbox className="w-12 h-12 text-muted-foreground mb-4" />
    <h3 className="text-lg font-semibold text-gray-900">No data available</h3>
    <p className="text-sm text-muted-foreground mt-2">Try adjusting your filters</p>
  </div>
);

/**
 * Error state component
 */
const ErrorState = ({ error }: { error?: string }) => (
  <div className="flex flex-col items-center justify-center h-64 text-center">
    <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
    <h3 className="text-lg font-semibold text-gray-900">Error loading data</h3>
    <p className="text-sm text-muted-foreground mt-2">{error || 'Please try again'}</p>
  </div>
);

/**
 * Leaderboard Visualization (DASH-7.2, DASH-24.2)
 * Shows ranked user metrics with medals for top 3
 * Wrapped in React.memo for performance
 * DASH-24.2: Added row click handler for drill-down
 */
export const LeaderboardVisualization = React.memo(function LeaderboardVisualization({
  data,
  config,
  onSegmentClick,
}: LeaderboardVisualizationProps) {
  const [currentPage, setCurrentPage] = useState(1);

  // Get data source label for column header
  const dataSourceLabel = useMemo(() => getDataSourceLabel(config.dataSource), [config.dataSource]);

  // Handle row click (DASH-24.2)
  // For leaderboards, pass the user name as clickedValue for display in drawer header
  // The full row data (with id) is passed in data for filter derivation
  const handleRowClick = useCallback(
    (row: any, index: number) => {
      if (onSegmentClick) {
        onSegmentClick({
          value: row.name || row.id,  // Prefer name for display, fallback to id
          index,
          data: row,
        });
      }
    },
    [onSegmentClick]
  );

  // Expect data format: [{ id, name, value }, ...]
  const leaderboardData = useMemo(() => {
    if (!Array.isArray(data.data)) return [];

    // Sort by value descending (highest first)
    return [...data.data].sort((a: any, b: any) => b.value - a.value);
  }, [data.data]);

  // Calculate pagination
  const totalPages = Math.ceil(leaderboardData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return leaderboardData.slice(start, end);
  }, [leaderboardData, currentPage]);

  // Handle page navigation
  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // Loading state
  if (!data.data) {
    return <LoadingSkeleton dataSourceLabel={dataSourceLabel} />;
  }

  // Empty state
  if (leaderboardData.length === 0) {
    return <EmptyState />;
  }

  // Error state (if error prop is passed)
  // Note: QueryResult doesn't have error field by default, but we check defensively
  if ((data as any).error) {
    return <ErrorState error={(data as any).error} />;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Rank</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">{dataSourceLabel}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((row: any, index: number) => {
              const rank = (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
              const actualIndex = (currentPage - 1) * ITEMS_PER_PAGE + index;
              return (
                <TableRow
                  key={row.id || index}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleRowClick(row, actualIndex)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleRowClick(row, actualIndex);
                    }
                  }}
                >
                  <TableCell className="p-4">
                    <RankIndicator rank={rank} />
                  </TableCell>
                  <TableCell className="p-4 font-medium">{row.name || 'Unknown'}</TableCell>
                  <TableCell className="p-4 text-right">
                    {formatValue(row.value || 0, config.aggregation.type)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-4 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
});
